import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/FilterBar";
import { MonthField, periodesRapides } from "@/components/DateField";
import { RepartirLogementsDialog } from "@/components/RepartirLogementsDialog";
import { ResaPastille } from "@/components/OperationBlock";
import { ReservationDetailDialog } from "@/components/ReservationDetailDialog";
import { api, errMsg, money } from "@/lib/api";
import { sansAccents } from "@/lib/partage";

const SOURCES = { manuel: "corrigé à la main", regle: "règle", reservation: "d'après la réservation", memorise: "mémorisé", description: "d'après la description" };
const FILTRES = [["tous", "Toutes les opérations"], ["airbnb", "Airbnb"], ["booking", "Booking"]];
const slug = (s) => s.replace(/\W+/g, "-").toLowerCase();
const trouve = (op, q) => !q || op.reservations.some((r) => sansAccents(`${r.code} ${r.client}`).includes(q));

const Recherche = ({ value, onChange }) => (
  <div className="relative mb-4 max-w-md">
    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Code de réservation ou nom du client…" className="h-10 bg-white pl-9 pr-9" data-testid="reaffecter-recherche" />
    {value && (
      <button type="button" onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700" title="Effacer" data-testid="reaffecter-recherche-effacer"><X className="h-4 w-4" /></button>
    )}
  </div>
);

const LigneOp = ({ op, coche, onCoche, cote, onRepartir, onResa }) => (
  <div className={`flex items-start gap-2 border-t border-slate-100 px-3 py-2 text-sm hover:bg-slate-50 ${coche ? "bg-emerald-50/70" : ""}`} data-testid={`${cote}-op-${op.id}`}>
    <Checkbox checked={coche} onCheckedChange={(v) => onCoche(op.id, !!v)} className="mt-0.5" data-testid={`${cote}-op-${op.id}-check`} />
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-slate-800">{op.description}</span>
        <span className={`shrink-0 font-medium tabular ${op.credit ? "text-emerald-700" : "text-red-700"}`}>{op.credit ? money(op.credit) : `− ${money(op.debit)}`}</span>
      </div>
      <span className="block text-xs text-slate-500">
        {op.date}{op.plateforme ? ` · ${op.plateforme}` : ""} · {SOURCES[op.source] || "auto"}
      </span>
      {op.reservations.length > 0 && (
        <span className="mt-1 flex flex-wrap gap-1.5" data-testid={`${cote}-op-${op.id}-reservations`}>
          {op.reservations.map((r, i) => <ResaPastille key={`${r.code}-${i}`} r={r} onClick={() => onResa(r.id)} testId={`${cote}-op-${op.id}-reservation-${i}`} />)}
        </span>
      )}
      {!op.partage && op.reservations.length > 1 && (
        <button type="button" className="mt-1 text-xs text-sky-700 underline-offset-2 hover:underline" onClick={() => onRepartir(op)} data-testid={`${cote}-op-${op.id}-partager`}>Partager entre les logements…</button>
      )}
      {op.partage && (
        <span className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded bg-sky-100 px-1.5 py-0.5 font-medium text-sky-800" data-testid={`${cote}-op-${op.id}-partage`}>Partagé · part {money(op.credit || op.debit)} sur {money(op.total)}</span>
          {(op.tps != null || op.tvq != null) && <span className="text-slate-500">TPS {money(op.tps || 0)} · TVQ {money(op.tvq || 0)}</span>}
          <button type="button" className="text-sky-700 underline-offset-2 hover:underline" onClick={() => onRepartir(op)} data-testid={`${cote}-op-${op.id}-repartir`}>Répartir…</button>
        </span>
      )}
    </div>
  </div>
);

const Colonne = ({ cote, etat, setEtat, logements, filtre, version, coches, setCoches, onOps, onRepartir, onResa, recherche }) => {
  const [ops, setOps] = useState(null);
  useEffect(() => {
    if (!etat.logement) return;
    setOps(null);
    api.get("/reaffecter/operations", { params: { mois: etat.mois, logement: etat.logement, filtre } })
      .then((r) => { setOps(r.data); onOps(r.data); }).catch((e) => { toast.error(errMsg(e)); setOps([]); onOps([]); });
  }, [etat.mois, etat.logement, filtre, version, onOps]);
  const cocher = (id, v) => { const s = new Set(coches); if (v) s.add(id); else s.delete(id); setCoches(s); };
  const q = sansAccents(recherche.trim());
  const vus = (ops || []).filter((o) => trouve(o, q));
  const total = vus.reduce((s, o) => s + (o.credit || 0) - (o.debit || 0), 0);
  return (
    <div className="flex min-h-[28rem] flex-col rounded-lg border border-slate-200 bg-white" data-testid={`${cote}-colonne`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 p-3">
        <MonthField value={etat.mois} onChange={(m) => { setEtat({ ...etat, mois: m }); setCoches(new Set()); }} className="w-36 bg-white" data-testid={`${cote}-mois`} />
        <Select value={etat.logement} onValueChange={(l) => { setEtat({ ...etat, logement: l }); setCoches(new Set()); }}>
          <SelectTrigger className="h-9 min-w-0 flex-1 bg-white" data-testid={`${cote}-logement`}><SelectValue placeholder="Choisir un logement" /></SelectTrigger>
          <SelectContent>{logements.map((l) => <SelectItem key={l} value={l} data-testid={`${cote}-logement-${slug(l)}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between px-3 py-2 text-xs text-slate-500">
        <span data-testid={`${cote}-compte`}>{ops ? `${q ? `${vus.length} sur ${ops.length}` : ops.length} opération(s) · ${coches.size} cochée(s)` : "Chargement…"}</span>
        {vus.length > 0 && <span className="tabular" data-testid={`${cote}-total`}>Net : {money(total)}</span>}
      </div>
      <div className="max-h-[32rem] flex-1 overflow-y-auto">
        {ops === null ? <Loader2 className="mx-auto mt-8 h-5 w-5 animate-spin text-slate-400" />
          : vus.length ? vus.map((o) => <LigneOp key={o.id} op={o} coche={coches.has(o.id)} onCoche={cocher} cote={cote} onRepartir={onRepartir} onResa={onResa} />)
            : <p className="p-6 text-center text-sm text-slate-400" data-testid={`${cote}-vide`}>{q && ops.length ? "Aucune opération ne correspond à la recherche." : "Aucune opération affectée à ce logement ce mois-ci."}</p>}
      </div>
    </div>
  );
};

const Fleches = ({ peutDroite, peutGauche, envoi, onDroite, onGauche }) => (
  <div className="flex flex-row items-center justify-center gap-3 lg:flex-col">
    <Button size="icon" className="h-11 w-11 bg-emerald-700 hover:bg-emerald-800" disabled={!peutDroite || envoi} onClick={onDroite} title="Déplacer la sélection de gauche vers le logement de droite" data-testid="deplacer-vers-droite">
      {envoi === "droite" ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5 rotate-90 lg:rotate-0" />}
    </Button>
    <Button size="icon" className="h-11 w-11 bg-emerald-700 hover:bg-emerald-800" disabled={!peutGauche || envoi} onClick={onGauche} title="Déplacer la sélection de droite vers le logement de gauche" data-testid="deplacer-vers-gauche">
      {envoi === "gauche" ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowLeft className="h-5 w-5 rotate-90 lg:rotate-0" />}
    </Button>
  </div>
);

export default function ReaffecterOperations() {
  const moisDefaut = format(periodesRapides()[1].debut, "yyyy-MM");
  const [logements, setLogements] = useState([]);
  const [filtre, setFiltre] = useState("tous");
  const [gauche, setGauche] = useState({ mois: moisDefaut, logement: "" });
  const [droite, setDroite] = useState({ mois: moisDefaut, logement: "" });
  const [cochesG, setCochesG] = useState(new Set());
  const [cochesD, setCochesD] = useState(new Set());
  const [version, setVersion] = useState(0);
  const [envoi, setEnvoi] = useState("");
  const [opsG, setOpsG] = useState([]);
  const [opsD, setOpsD] = useState([]);
  const [dialogue, setDialogue] = useState(null);
  const [resa, setResa] = useState(null);
  const [recherche, setRecherche] = useState("");
  useEffect(() => {
    api.get("/reaffecter/logements").then(({ data }) => {
      setLogements(data);
      const courts = data.filter((l) => /airbnb|booking/i.test(l));
      const [a, b] = courts.length >= 2 ? courts : data;
      setGauche((g) => ({ ...g, logement: g.logement || a || "" }));
      setDroite((d) => ({ ...d, logement: d.logement || b || a || "" }));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const recharger = () => { setCochesG(new Set()); setCochesD(new Set()); setVersion((v) => v + 1); };
  const deplacer = useCallback(async (sens) => {
    const [ids, cible, source, ops] = sens === "droite" ? [cochesG, droite.logement, gauche.logement, opsG] : [cochesD, gauche.logement, droite.logement, opsD];
    const choisies = ops.filter((o) => ids.has(o.id));
    const partages = choisies.filter((o) => o.partage);
    const normales = choisies.filter((o) => !o.partage).map((o) => o.id);
    if (normales.length) {
      setEnvoi(sens);
      try {
        const { data } = await api.post("/reaffecter/operations", { ids: normales, logement: cible });
        toast.success(`${data.trouvees} opération(s) affectée(s) à ${cible}`);
      } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(""); }
    }
    if (partages.length) {
      if (partages.length > 1) toast.info(`${partages.length} versements partagés : répartissez-les un par un (bouton « Répartir… »)`);
      setDialogue({ op: partages[0], logements: [source, cible] });
    }
    if (normales.length && !partages.length) recharger();
  }, [cochesG, cochesD, gauche.logement, droite.logement, opsG, opsD]);
  const memeLogement = gauche.logement === droite.logement;
  return (
    <div data-testid="reaffecter-page">
      <PageHeader eyebrow="Corrections" title="Réaffecter les opérations" />
      <p className="-mt-4 mb-4 max-w-3xl text-sm text-slate-600">Corrigez le logement attribué automatiquement à un dépôt ou un retrait du relevé : cochez une ou plusieurs opérations, puis cliquez sur la flèche pour les affecter au logement d'en face. Les réservations ne bougent pas, et la correction est gardée lors des prochains imports.</p>
      <RadioGroup value={filtre} onValueChange={(v) => { setFiltre(v); setCochesG(new Set()); setCochesD(new Set()); }} className="mb-4 flex flex-wrap gap-4" data-testid="reaffecter-filtre">
        {FILTRES.map(([v, l]) => (
          <label key={v} className="flex cursor-pointer items-center gap-2 text-sm"><RadioGroupItem value={v} data-testid={`reaffecter-filtre-${v}`} />{l}</label>
        ))}
      </RadioGroup>
      <Recherche value={recherche} onChange={(v) => { setRecherche(v); setCochesG(new Set()); setCochesD(new Set()); }} />
      {memeLogement && gauche.logement && <p className="mb-3 text-xs text-amber-700" data-testid="reaffecter-meme-logement">Choisissez deux logements différents pour pouvoir déplacer.</p>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto_1fr]">
        <Colonne cote="gauche" etat={gauche} setEtat={setGauche} logements={logements} filtre={filtre} version={version} coches={cochesG} setCoches={setCochesG} onOps={setOpsG} onResa={setResa} recherche={recherche} onRepartir={(op) => setDialogue({ op, logements: [gauche.logement, droite.logement] })} />
        <Fleches envoi={envoi} peutDroite={cochesG.size > 0 && !memeLogement} peutGauche={cochesD.size > 0 && !memeLogement} onDroite={() => deplacer("droite")} onGauche={() => deplacer("gauche")} />
        <Colonne cote="droite" etat={droite} setEtat={setDroite} logements={logements} filtre={filtre} version={version} coches={cochesD} setCoches={setCochesD} onOps={setOpsD} onResa={setResa} recherche={recherche} onRepartir={(op) => setDialogue({ op, logements: [droite.logement, gauche.logement] })} />
      </div>
      {resa && <ReservationDetailDialog rid={resa} onClose={() => setResa(null)} />}
      {dialogue && <RepartirLogementsDialog op={dialogue.op} logements={dialogue.logements} onClose={() => { setDialogue(null); recharger(); }} onSaved={() => { setDialogue(null); recharger(); }} />}
    </div>
  );
}
